import IdentityHeader from "@/components/common/IdentityHeader";
import StatusBadge from "@/components/common/StatusBadge";
import { NavigationTabs } from "@/components/common/NavigationTabs";
import DataTableActionMenu from "@/components/datatable/components/TableActionMenu";
import ProfileSkeleton from "@/components/preview/ProfileSkeleton";
import StatStrip from "@/components/common/StatStrip";
import useConfirm from "@/hooks/useConfirm";
import useFormatter from "@/hooks/useFormatter";
import { getInitials, getUserAvatarColorClass } from "@/lib/utils";
import { usersKeys, kpisKeys } from "@/query-keys";
import { services } from "@/services";
import { Outlet, createFileRoute, useLocation } from "@tanstack/react-router";
import { BanknoteArrowUp, Calendar, Clock, Hammer, Mail, Phone, User, UserX } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { User as UserType } from "@/schemas/userSchema";
import useUserQuery from "../_hooks/useUserQuery";
import useUserMutation from "../_hooks/useUserMutation";
import useUserKpis from "../_hooks/useUserKpis";

export const Route = createFileRoute("/_private/users/$userId")({
  staticData: {
    breadcrumb: (matched) => ({ label: matched.loaderData?.name, translate: false }),
  },
  loader: async ({ params, context: { queryClient } }) => {
    const user = await queryClient.ensureQueryData({
      queryKey: usersKeys.detail(params.userId),
      queryFn: () => services.getUser(params.userId),
    });
    queryClient.ensureQueryData({
      queryKey: kpisKeys.users(params.userId),
      queryFn: () => services.getUserKpis(params.userId),
    });
    return user;
  },
  component: RouteComponent,
  pendingComponent: ProfileSkeleton,
});

const TABS = [
  { value: "icash-transactions", icon: BanknoteArrowUp, labelKey: "kpis.icashTransactions" },
  { value: "igold-transactions", icon: Hammer, labelKey: "kpis.igoldTransactions" },
] as const;

function RouteComponent() {
  const navigate = Route.useNavigate();
  const params = Route.useParams();
  const loaderData = Route.useLoaderData();
  const pathname = useLocation({ select: (location) => location.pathname });
  const { t } = useTranslation();
  const { phone, weight, currency, date } = useFormatter();
  const { data } = useUserQuery(params.userId, loaderData);
  const { deleteUser, toggleActiveStatus, isDeleting, isToggling } = useUserMutation();
  const { data: kpis } = useUserKpis(params.userId);

  const userTabs = useMemo(() => TABS.map((tab) => ({ ...tab, label: t(tab.labelKey) })), [t]);

  const getCurrentTab = () => {
    if (pathname.includes("/igold-transactions")) return "igold-transactions";
    return "icash-transactions";
  };

  const handleTabChange = (value: string) => {
    switch (value) {
      case "igold-transactions":
        navigate({ to: "/users/$userId/igold-transactions", params });
        break;
      default:
        navigate({ to: "/users/$userId/icash-transactions", params });
        break;
    }
  };

  if (!data) return null;

  const user = data;
  const isDeleted = !!user.deletedAt;

  const deleteConfirm = useConfirm({
    title: t("users.confirm.delete.title"),
    description: t("users.confirm.delete.description"),
    isPending: isDeleting,
  });

  const deactivateConfirm = useConfirm({
    title: t("users.confirm.deactivate.title"),
    description: t("users.confirm.deactivate.description"),
    isPending: isToggling,
  });

  const actions = useMemo(
    () => [
      {
        icon: (row: UserType) =>
          row.isActive ? (
            <UserX className="h-4 w-4 shrink-0" />
          ) : (
            <User className="h-4 w-4 shrink-0" />
          ),
        label: (user: UserType) => (user.isActive ? t("common.deactivate") : t("common.activate")),
        handler: (user: UserType) =>
          deactivateConfirm.confirm(() => void toggleActiveStatus(user.id)),
      },
    ],
    [t, deactivateConfirm, toggleActiveStatus],
  );

  const colorClass = getUserAvatarColorClass(user.role);

  return (
    <>
      <IdentityHeader
        initials={getInitials(user.name)}
        avatarColorClass={!isDeleted ? colorClass : undefined}
        name={user.name}
        badge={
          <>
            <StatusBadge label={t(`common.${user.role}`)} variant={user.role} />
            <StatusBadge
              variant={user.isActive ? "active" : "inactive"}
              label={user.isActive ? t("common.active") : t("common.inactive")}
            />
          </>
        }
        subLabel={
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-1" title={user.email}>
              <Mail className="h-3.5 w-3.5 shrink-0" />
              {user.email}
            </span>
            {user.phone && (
              <span className="inline-flex items-center gap-1" dir="ltr">
                <Phone className="h-3.5 w-3.5 shrink-0" />
                {phone(user.phone)}
              </span>
            )}
          </div>
        }
        metaLabel={
          <span className="flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {t("users.fields.createdAt")} {date(user.createdAt)}
            </span>
            {user.updatedAt && (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {t("users.fields.updatedAt")} {date(user.updatedAt)}
              </span>
            )}
            {user.createdByName && (
              <span className="inline-flex items-center gap-1">
                <User className="h-3 w-3" />
                {t("users.fields.createdBy")} {user.createdByName}
              </span>
            )}
          </span>
        }
        isDeleted={isDeleted}
      >
        <DataTableActionMenu
          onDelete={() => deleteConfirm.confirm(() => void deleteUser(user.id))}
          row={user}
          actions={actions}
        />
      </IdentityHeader>

      {kpis && (
        <StatStrip
          columns={[
            {
              dotClass: "bg-muted-foreground",
              label: t("kpis.userIcashTotalExpenses"),
              value: currency(kpis.icashTotalExpenses),
              subValue: (
                <span>
                  {t("kpis.userIcashTotalWithdrawn")}: {currency(kpis.icashTotalWithdrawn)}
                  {" · "}
                  {t("kpis.userIcashTotalLost")}: {currency(kpis.icashTotalLost)}
                </span>
              ),
            },
            {
              dotClass: "bg-primary",
              label: t("kpis.userActiveGoldOutstanding"),
              value: weight(kpis.activeGoldOutstanding),
              subValue: (
                <span>
                  {t("kpis.userIgoldTotalLost")}: {weight(kpis.igoldUserTotalLost)}
                  {" · "}
                  {t("kpis.userIgoldEmployeeTotalLost")}: {weight(kpis.igoldEmployeeTotalLost)}
                </span>
              ),
            },
          ]}
        />
      )}

      <NavigationTabs activeTab={getCurrentTab()} onTabChange={handleTabChange} tabs={userTabs}>
        <Outlet />
      </NavigationTabs>

      {deleteConfirm.confirmDialog}
      {deactivateConfirm.confirmDialog}
    </>
  );
}
